import {
  CheckFeatureAccessHeader,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { VITE_UTILITIES_BASE_URL } from "@src/env";
import request from "@src/global_functions/request";
import { camelCase } from "lodash";
import { CamelCasedProperties } from "type-fest";
import { Utility } from "../../../types";
import { recursiveCamelCaseCipher } from "../../postgrestApi";
import { ClientOutput } from "../client";
import { UnitParam } from "./bill";
import { UTILITY_BILL_CONSUMPTION_KEY } from "./consumption";

// INNO-765: can use generated types with no modifications once done
export type BillInvoiceSnakeCase = Omit<
  ClientOutput["utilities"]["BillInvoice"],
  "taxes_and_other" | "total_cost" | "total_consumption" | "blended_rate"
> & {
  taxes_and_other?: number | null;
  total_cost?: number | null;
  total_consumption: number;
  blended_rate: number;
};

export type BillInvoice = Omit<
  CamelCasedProperties<BillInvoiceSnakeCase>,
  "utilityType"
> & { utilityType: Utility };

const billInvoice = {
  useQueryAll,
} as const;

export default billInvoice;

// endpoint requires {feature: "Utilities", access: "read"}
const getBillInvoices = (
  site_id: number,
  unit: UnitParam,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  // use superagent here as fake timer/promise flushing in test does not seem to resolve promise
  // when msw rest.get handler intercepts fetch request
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/${site_id}/bill_invoices`)
    .set(checkFeatureAccessHeader)
    .query({ unit })
    .then(
      ({ body }: { body: BillInvoiceSnakeCase[] }) =>
        body.map(({ utility_type, ...invoice }) => ({
          ...recursiveCamelCaseCipher(invoice),
          utilityType: camelCase(utility_type),
        })) as BillInvoice[]
    )
    .catch((e) => {
      throw new Error(e);
    });

function useQueryAll(siteId: number, unit: UnitParam) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getBillInvoices(siteId, unit, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_BILL_CONSUMPTION_KEY, siteId, "bill-invoice", unit],
    }
  );
}
